import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { journalsService } from '../services/journals.service';
import type { CreateEntryPayload, UpdateEntryPayload } from '../types/api.types';
import toast from 'react-hot-toast';

export const useJournalEntries = (journalId: string) =>
  useQuery({
    queryKey: ['journals', journalId, 'entries'],
    queryFn: () => journalsService.listEntries(journalId),
    enabled: !!journalId,
    staleTime: 1000 * 60 * 2,
  });

export const useJournalEntry = (journalId: string, entryId: string) =>
  useQuery({
    queryKey: ['journals', journalId, 'entries', entryId],
    queryFn: () => journalsService.getEntry(journalId, entryId),
    enabled: !!journalId && !!entryId,
    staleTime: 1000 * 60 * 2,
  });

export const useCreateEntry = (journalId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEntryPayload) =>
      journalsService.createEntry(journalId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journals', journalId, 'entries'] });
    },
    onError: () => toast.error('Failed to save entry'),
  });
};

export const useUpdateEntry = (journalId: string, entryId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateEntryPayload) =>
      journalsService.updateEntry(journalId, entryId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journals', journalId, 'entries', entryId] });
      queryClient.invalidateQueries({ queryKey: ['journals', journalId, 'entries'] });
    },
    onError: () => toast.error('Failed to save entry'),
  });
};

export const useDeleteEntry = (journalId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => journalsService.deleteEntry(journalId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journals', journalId, 'entries'] });
      toast.success('Entry deleted');
    },
    onError: () => toast.error('Failed to delete entry'),
  });
};
